import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Upload, Eye, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp,
  Info, RefreshCw, Server, Network, Cable, Layers, Loader2,
  FlaskConical, Zap, Shield, BookmarkCheck
} from 'lucide-react'
import { api } from '../utils/api'
import toast from 'react-hot-toast'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExportSummary {
  racks:      { create: number; update: number; skip: number; total: number }
  devices:    { create: number; update: number; skip: number; total: number }
  interfaces: { create: number; update: number; skip: number; total: number }
  cables:     { create: number; update: number; skip: number; total: number }
  warnings:   number
}

interface ExportRackItem {
  local_id:   number
  name:       string
  u_height:   number
  action:     'create' | 'update' | 'skip'
  netbox_id?: number
  row_label:  string
}

interface ExportPreviewData {
  summary:    ExportSummary
  racks:      ExportRackItem[]
  devices:    any[]
  interfaces: any[]
  cables:     any[]
  warnings:   string[]
}

interface ExportStats {
  racks_created:      number
  racks_updated:      number
  racks_skipped:      number
  devices_created:    number
  devices_updated:    number
  devices_skipped:    number
  interfaces_created: number
  interfaces_skipped: number
  cables_created:     number
  cables_skipped:     number
  errors:             { type: string; name?: string; error: string }[]
}

// ---------------------------------------------------------------------------
// Action badge
// ---------------------------------------------------------------------------

function ActionBadge({ action }: { action: string }) {
  const cfg: Record<string, { cls: string; label: string }> = {
    create: { cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', label: '+Создать' },
    update: { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30',  label: '~Обновить' },
    skip:   { cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30',   label: '=Пропустить' },
  }
  const c = cfg[action] || cfg.skip
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${c.cls}`}>{c.label}</span>
  )
}

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------

function SummaryCard({
  icon: Icon, label, counts, color
}: {
  icon: any; label: string
  counts: { create: number; update: number; skip: number; total: number }
  color: string
}) {
  return (
    <div className="bg-bg-panel border border-border rounded-xl p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="ml-auto text-xs text-text-muted">{counts.total} объектов</span>
      </div>
      <div className="flex gap-3 text-xs">
        {counts.create > 0 && <span className="text-emerald-400">+{counts.create} создать</span>}
        {counts.update > 0 && <span className="text-amber-400">~{counts.update} обновить</span>}
        {counts.skip   > 0 && <span className="text-slate-400">={counts.skip} пропустить</span>}
        {counts.total  === 0 && <span className="text-slate-500 italic">нет данных</span>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1: Connection (reuse saved connection + conflict strategy)
// ---------------------------------------------------------------------------

function ExportConnectionStep({
  dcCode, onReady
}: {
  dcCode: string
  onReady: (url: string, token: string, strategy: string) => void
}) {
  const [url, setUrl]           = useState('')
  const [token, setToken]       = useState('')
  const [strategy, setStrategy] = useState<'local_wins' | 'skip_existing'>('local_wins')
  const [useSaved, setUseSaved] = useState(true)
  const [checking, setChecking] = useState(false)
  const [savedHint, setSavedHint] = useState<string | null>(null)

  // Auto-load saved connection hint
  useState(() => {
    api.get(`/api/integrations/netbox/connection?dc_code=${dcCode}`)
      .then(res => {
        if (res.data?.saved) setSavedHint(res.data.token_hint || '****')
      })
      .catch(() => {})
  })

  async function handleCheck() {
    setChecking(true)
    try {
      const payload: any = { dc_code: dcCode, conflict_strategy: strategy, use_saved: useSaved }
      if (!useSaved || url) payload.url = url
      if (!useSaved || token) payload.token = token

      const res = await api.post('/api/integrations/netbox/export/preview', payload)
      const preview = res.data?.preview
      if (preview) {
        toast.success('Подключение успешно — предпросмотр готов')
        onReady(url, token, strategy)
      }
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message
      toast.error(`Ошибка: ${msg}`)
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="space-y-5">
      {savedHint && (
        <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-lg px-4 py-2.5">
          <BookmarkCheck className="w-4 h-4 shrink-0" />
          Сохранённое подключение найдено (токен: ···{savedHint})
        </div>
      )}

      <div className="flex items-center gap-2">
        <input type="checkbox" id="use-saved-exp" checked={useSaved}
          onChange={e => setUseSaved(e.target.checked)}
          className="rounded border-border" />
        <label htmlFor="use-saved-exp" className="text-sm text-text-secondary">
          Использовать сохранённое подключение
        </label>
      </div>

      {!useSaved && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-text-muted mb-1">NetBox URL</label>
            <input
              value={url} onChange={e => setUrl(e.target.value)}
              placeholder="http://netbox.example.com"
              className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-sm text-text-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">API Token</label>
            <input
              type="password" value={token} onChange={e => setToken(e.target.value)}
              placeholder="токен NetBox API"
              className="w-full px-3 py-2 rounded-lg border border-border bg-bg-input text-sm text-text-primary"
            />
          </div>
        </div>
      )}

      {/* Conflict strategy */}
      <div className="bg-bg-panel border border-border rounded-xl p-4 space-y-2">
        <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Стратегия конфликтов</p>
        {[
          { value: 'local_wins',    label: 'Локальные данные → NetBox',   desc: 'Перезаписать существующие объекты в NetBox', icon: Shield },
          { value: 'skip_existing', label: 'Только новые объекты',         desc: 'Пропускать объекты, уже существующие в NetBox', icon: Layers },
        ].map(opt => (
          <label key={opt.value}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
              strategy === opt.value
                ? 'border-accent-primary/50 bg-accent-primary/5'
                : 'border-border hover:border-border-hover'
            }`}
          >
            <input type="radio" name="export-strategy" value={opt.value}
              checked={strategy === opt.value}
              onChange={() => setStrategy(opt.value as any)}
              className="mt-0.5" />
            <div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                <opt.icon className="w-3.5 h-3.5" />{opt.label}
              </div>
              <div className="text-xs text-text-muted mt-0.5">{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>

      <button
        onClick={handleCheck}
        disabled={checking || (!useSaved && (!url || !token))}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-accent-primary text-white text-sm font-medium hover:bg-accent-primary-hover disabled:opacity-50 transition"
      >
        {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
        {checking ? 'Проверяем…' : 'Предпросмотр экспорта →'}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2: Preview
// ---------------------------------------------------------------------------

function ExportPreviewStep({
  dcCode, url, token, strategy,
  onConfirm
}: {
  dcCode: string; url: string; token: string; strategy: string
  onConfirm: (selectedRackIds: number[] | null) => void
}) {
  const [loading, setLoading]           = useState(false)
  const [preview, setPreview]           = useState<ExportPreviewData | null>(null)
  const [selectedRackIds, setSelected]  = useState<Set<number>>(new Set())
  const [showWarnings, setShowWarnings] = useState(false)
  const [allSelected, setAllSelected]   = useState(true)

  async function loadPreview() {
    setLoading(true)
    try {
      const res = await api.post('/api/integrations/netbox/export/preview', {
        dc_code: dcCode, url: url || undefined, token: token || undefined,
        conflict_strategy: strategy, use_saved: !url || !token,
      })
      const data = res.data?.preview as ExportPreviewData
      setPreview(data)
      setSelected(new Set(data.racks.map(r => r.local_id)))
      setAllSelected(true)
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка предпросмотра')
    } finally {
      setLoading(false)
    }
  }

  function toggleRack(id: number) {
    setSelected(prev => {
      const s = new Set(prev)
      s.has(id) ? s.delete(id) : s.add(id)
      return s
    })
  }

  function toggleAll() {
    if (!preview) return
    if (allSelected) { setSelected(new Set()); setAllSelected(false) }
    else { setSelected(new Set(preview.racks.map(r => r.local_id))); setAllSelected(true) }
  }

  const hasActioned = preview && preview.racks.some(r => r.action !== 'skip')

  return (
    <div className="space-y-4">
      {!preview ? (
        <button
          onClick={loadPreview} disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border text-sm text-text-secondary hover:text-text-primary hover:border-accent-primary/30 transition"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {loading ? 'Загружаем…' : 'Получить предпросмотр'}
        </button>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard icon={Layers}  label="Стойки"      counts={preview.summary.racks}      color="bg-cyan-500/15 text-cyan-400" />
            <SummaryCard icon={Server}  label="Устройства"  counts={preview.summary.devices}    color="bg-violet-500/15 text-violet-400" />
            <SummaryCard icon={Network} label="Интерфейсы"  counts={preview.summary.interfaces} color="bg-emerald-500/15 text-emerald-400" />
            <SummaryCard icon={Cable}   label="Кабели"      counts={preview.summary.cables}     color="bg-amber-500/15 text-amber-400" />
          </div>

          {/* Warnings */}
          {preview.warnings.length > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 overflow-hidden">
              <button
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-amber-400"
                onClick={() => setShowWarnings(v => !v)}
              >
                <AlertTriangle className="w-4 h-4" />
                {preview.warnings.length} предупреждений
                {showWarnings ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
              </button>
              {showWarnings && (
                <ul className="px-4 pb-3 space-y-1">
                  {preview.warnings.map((w, i) => (
                    <li key={i} className="text-xs text-amber-300/80">• {w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Rack selector */}
          {hasActioned && (
            <div className="bg-bg-panel border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
                  Выбор стоек для экспорта
                </span>
                <button onClick={toggleAll} className="text-xs text-accent-primary hover:underline">
                  {allSelected ? 'Снять все' : 'Выбрать все'}
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-border">
                {preview.racks.map(rack => (
                  <label key={rack.local_id}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-bg-hover cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRackIds.has(rack.local_id)}
                      onChange={() => toggleRack(rack.local_id)}
                      className="rounded border-border"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-primary truncate">{rack.name}</span>
                        <ActionBadge action={rack.action} />
                      </div>
                      <span className="text-xs text-text-muted">{rack.row_label} · {rack.u_height}U</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => onConfirm(selectedRackIds.size > 0 ? [...selectedRackIds] : null)}
            disabled={selectedRackIds.size === 0 && !!hasActioned}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-accent-primary text-white text-sm font-medium hover:bg-accent-primary-hover disabled:opacity-50 transition"
          >
            <Upload className="w-4 h-4" />
            Экспортировать в NetBox →
          </button>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3: Apply
// ---------------------------------------------------------------------------

function ExportApplyStep({
  dcCode, url, token, strategy, selectedRackIds
}: {
  dcCode: string; url: string; token: string; strategy: string
  selectedRackIds: number[] | null
}) {
  const [dryRun, setDryRun]     = useState(true)
  const [running, setRunning]   = useState(false)
  const [stats, setStats]       = useState<ExportStats | null>(null)
  const [success, setSuccess]   = useState<boolean | null>(null)

  async function run(isDry: boolean) {
    setRunning(true)
    setStats(null)
    try {
      const res = await api.post('/api/integrations/netbox/export/apply', {
        dc_code: dcCode,
        url: url || undefined,
        token: token || undefined,
        conflict_strategy: strategy,
        dry_run: isDry,
        use_saved: !url || !token,
        selected_rack_ids: selectedRackIds,
      })
      setStats(res.data.stats)
      setSuccess(res.data.success)
      if (res.data.success) {
        toast.success(isDry ? 'Dry Run завершён без ошибок' : 'Экспорт завершён!')
      } else {
        toast.error('Экспорт завершён с ошибками')
      }
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка экспорта')
      setSuccess(false)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-sm text-blue-300">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Данные будут отправлены в NetBox</p>
          <p className="text-xs text-blue-300/70 mt-0.5">
            {selectedRackIds
              ? `Выбрано стоек: ${selectedRackIds.length}`
              : 'Все стойки ЦОД'}
          </p>
        </div>
      </div>

      {/* Dry run toggle */}
      <div className="flex items-center gap-3 px-4 py-3 bg-bg-panel border border-border rounded-xl">
        <FlaskConical className="w-4 h-4 text-violet-400 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-text-primary">Dry Run (тест)</p>
          <p className="text-xs text-text-muted">Симуляция без реальных изменений в NetBox</p>
        </div>
        <button
          onClick={() => setDryRun(v => !v)}
          className={`relative w-10 h-5 rounded-full transition-colors ${dryRun ? 'bg-violet-500' : 'bg-slate-600'}`}
        >
          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${dryRun ? 'left-0.5' : 'left-5'}`} />
        </button>
      </div>

      {/* Run buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => run(true)}
          disabled={running}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-violet-500/40 text-violet-400 text-sm hover:bg-violet-500/10 disabled:opacity-50 transition"
        >
          {running && dryRun ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Dry Run
        </button>
        <button
          onClick={() => run(false)}
          disabled={running}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-500 disabled:opacity-50 transition"
        >
          {running && !dryRun ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Применить
        </button>
      </div>

      {/* Results */}
      {stats && (
        <div className={`rounded-xl border p-4 space-y-3 ${success ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            {success
              ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              : <AlertTriangle className="w-4 h-4 text-red-400" />}
            <span className={success ? 'text-emerald-300' : 'text-red-300'}>
              {success ? 'Успешно' : `Ошибок: ${stats.errors.length}`}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-text-muted">
            {[
              [`Стоек создано`,      stats.racks_created],
              [`Стоек обновлено`,    stats.racks_updated],
              [`Устройств создано`,  stats.devices_created],
              [`Устройств обновлено`,stats.devices_updated],
              [`Интерфейсов`,        stats.interfaces_created],
              [`Кабелей`,            stats.cables_created],
            ].map(([label, val]) => (
              <div key={label as string} className="flex justify-between">
                <span>{label}</span>
                <span className={`font-mono font-medium ${(val as number) > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                  {val}
                </span>
              </div>
            ))}
          </div>
          {stats.errors.length > 0 && (
            <div className="space-y-1.5 pt-1 border-t border-red-500/20">
              {stats.errors.map((err, i) => (
                <div key={i} className="text-xs text-red-300/80 font-mono">
                  [{err.type}] {err.name && `${err.name}: `}{err.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main wizard
// ---------------------------------------------------------------------------

const STEPS = [
  { icon: Network, label: 'Подключение' },
  { icon: Eye,     label: 'Предпросмотр' },
  { icon: Upload,  label: 'Экспорт' },
]

export default function NetBoxExportWizard() {
  const params = useParams<{ dcCode?: string }>()
  const dcCode = params.dcCode || ''

  const [step, setStep]                   = useState<0 | 1 | 2>(0)
  const [connUrl, setConnUrl]             = useState('')
  const [connToken, setConnToken]         = useState('')
  const [strategy, setStrategy]           = useState('local_wins')
  const [selectedRackIds, setSelected]    = useState<number[] | null>(null)

  function handleConnectionReady(url: string, token: string, strat: string) {
    setConnUrl(url); setConnToken(token); setStrategy(strat)
    setStep(1)
  }

  function handlePreviewConfirm(rackIds: number[] | null) {
    setSelected(rackIds)
    setStep(2)
  }

  return (
    <div className="flex flex-col h-full bg-bg-base">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <h1 className="text-xl font-semibold text-text-primary flex items-center gap-2">
          <Upload className="w-5 h-5 text-accent-primary" />
          Экспорт в NetBox
        </h1>
        <p className="text-sm text-text-muted mt-0.5">
          Синхронизация стоек, устройств, портов и кабелей из DC Cable Link в NetBox
        </p>
      </div>

      {/* Step indicator */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => {
            const Icon = s.icon
            const done   = i < step
            const active = i === step
            return (
              <div key={i} className="flex items-center">
                <button
                  onClick={() => i < step && setStep(i as any)}
                  disabled={i > step}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    active  ? 'bg-accent-primary/10 text-accent-primary' :
                    done    ? 'text-green-400 hover:bg-green-400/10 cursor-pointer' :
                              'text-text-muted cursor-default'
                  }`}
                >
                  {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  {s.label}
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`w-8 h-px mx-1 ${i < step ? 'bg-green-500/50' : 'bg-border'}`} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8">
          {step === 0 && (
            <ExportConnectionStep
              dcCode={dcCode}
              onReady={handleConnectionReady}
            />
          )}
          {step === 1 && (
            <ExportPreviewStep
              dcCode={dcCode}
              url={connUrl}
              token={connToken}
              strategy={strategy}
              onConfirm={handlePreviewConfirm}
            />
          )}
          {step === 2 && (
            <ExportApplyStep
              dcCode={dcCode}
              url={connUrl}
              token={connToken}
              strategy={strategy}
              selectedRackIds={selectedRackIds}
            />
          )}
        </div>
      </div>
    </div>
  )
}
